/**
 * Shared tools for the three mockup editors (classic, AI studio, text).
 *
 *   MDMockup.ppi(car, canvasWidth)      pixels per real inch on a car photo
 *   MDMockup.prepareLogo(img, opts)     trimmed logo canvas, background optional
 *   MDMockup.edgeBackground(img)        is there a solid background to offer removing?
 *   MDMockup.fitInches(...)             real magnet size for a logo and plan
 *   MDSaveDesign.mount(opts)            "Save your design" card, email button, exit prompt
 *
 * Nothing here erases pixels unless the customer asks for it: the old editor
 * turned every near-white pixel transparent, which also deleted white letters
 * inside logos and made the preview differ from the printed file.
 */
(function () {
  'use strict';

  /* All five car photos draw the vehicle across a fixed share of the image
     width. Real lengths are typical for each body style; together they turn
     a 23-inch-tall magnet into the right number of pixels on each photo. */
  var CARS = {
    compact: { lengthIn: 168, span: 0.828 },
    sports:  { lengthIn: 178, span: 0.909 },
    sedan:   { lengthIn: 192, span: 0.909 },
    suv:     { lengthIn: 210, span: 0.891 },
    pickup:  { lengthIn: 231, span: 0.909 }
  };
  var PLAN_WIDTH_IN = { small: 35, medium: 55, large: 75 };
  var PLAN_HEIGHT_IN = 23;
  /* Uncut margin the cutter leaves around the artwork, in inches. */
  var CUT_MARGIN_IN = 0.6;

  function ppi(car, canvasWidth) {
    var c = CARS[car] || CARS.sedan;
    return canvasWidth * c.span / c.lengthIn;
  }

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  function sourceSize(img) {
    return {
      w: img.naturalWidth || img.width || 1,
      h: img.naturalHeight || img.height || 1
    };
  }

  /* Work on a copy capped at 1600 px: enough for any preview, and keeps the
     pixel loops below fast on phones. */
  function toCanvas(img, max) {
    var s = sourceSize(img);
    var k = Math.min(1, (max || 1600) / Math.max(s.w, s.h));
    var c = makeCanvas(s.w * k, s.h * k);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c;
  }

  function readPixels(c) {
    try { return c.getContext('2d').getImageData(0, 0, c.width, c.height); }
    catch (e) { return null; }
  }

  /* A background is "solid" when the image border is opaque and nearly one
     colour all the way round. Only then is removing it offered. */
  function edgeBackground(img) {
    var c = img.getContext ? img : toCanvas(img);
    var d = readPixels(c);
    if (!d) return null;
    var w = c.width, h = c.height, p = d.data;
    var samples = [], step = Math.max(1, Math.floor((w + h) / 200));
    for (var x = 0; x < w; x += step) { samples.push(x, (h - 1) * w + x); }
    for (var y = 0; y < h; y += step) { samples.push(y * w, y * w + w - 1); }
    var r = 0, g = 0, b = 0, opaque = 0;
    samples.forEach(function (j) {
      var i = j * 4;
      if (p[i + 3] > 240) { opaque++; r += p[i]; g += p[i + 1]; b += p[i + 2]; }
    });
    if (opaque < samples.length * 0.9) return null;
    r /= opaque; g /= opaque; b /= opaque;
    var close = 0;
    samples.forEach(function (j) {
      var i = j * 4;
      if (Math.abs(p[i] - r) + Math.abs(p[i + 1] - g) + Math.abs(p[i + 2] - b) < 60) close++;
    });
    if (close < samples.length * 0.85) return null;
    return { r: Math.round(r), g: Math.round(g), b: Math.round(b) };
  }

  /* Flood fill from the border: removes the background colour only where it
     touches the edge, so white letters and shapes inside the logo survive. */
  function removeBackground(c, bg) {
    var d = readPixels(c);
    if (!d || !bg) return c;
    var w = c.width, h = c.height, p = d.data;
    var seen = new Uint8Array(w * h), stack = [];
    for (var x = 0; x < w; x++) stack.push(x, (h - 1) * w + x);
    for (var y = 0; y < h; y++) stack.push(y * w, y * w + w - 1);
    while (stack.length) {
      var j = stack.pop();
      if (seen[j]) continue;
      seen[j] = 1;
      var i = j * 4;
      var dist = Math.abs(p[i] - bg.r) + Math.abs(p[i + 1] - bg.g) + Math.abs(p[i + 2] - bg.b);
      if (p[i + 3] > 10 && dist > 60) continue;
      /* Soften the edge instead of a hard cut, so anti-aliased outlines stay smooth. */
      p[i + 3] = dist > 30 ? Math.round(p[i + 3] * (dist - 30) / 30) : 0;
      var cx = j % w, cy = (j / w) | 0;
      if (cx > 0) stack.push(j - 1);
      if (cx < w - 1) stack.push(j + 1);
      if (cy > 0) stack.push(j - w);
      if (cy < h - 1) stack.push(j + w);
    }
    c.getContext('2d').putImageData(d, 0, 0);
    return c;
  }

  /* Trim fully transparent margins; opaque pixels of any colour, white
     included, count as artwork. */
  function alphaTrim(c) {
    var d = readPixels(c);
    if (!d) return c;
    var w = c.width, h = c.height, p = d.data;
    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (p[(y * w + x) * 4 + 3] > 12) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < x0 || y1 < y0) return c;
    if (x0 === 0 && y0 === 0 && x1 === w - 1 && y1 === h - 1) return c;
    var out = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
    out.getContext('2d').drawImage(c, -x0, -y0);
    return out;
  }

  function tint(c, colour) {
    var out = makeCanvas(c.width, c.height), g = out.getContext('2d');
    g.drawImage(c, 0, 0);
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = colour;
    g.fillRect(0, 0, out.width, out.height);
    return out;
  }

  /* opts: { removeBg: bool, colour: css colour or 'original', max: longest side in px } */
  function prepareLogo(img, opts) {
    opts = opts || {};
    var c = toCanvas(img, opts.max);
    var bg = opts.removeBg ? edgeBackground(c) : null;
    if (bg) removeBackground(c, bg);
    c = alphaTrim(c);
    if (opts.colour && opts.colour !== 'original') c = tint(c, opts.colour);
    return c;
  }

  /* Largest logo that fits the plan's maximum area, minus the cut margin.
     Returns real inches of the finished magnet (artwork + margin). */
  function fitInches(logoW, logoH, plan) {
    var maxW = (PLAN_WIDTH_IN[plan] || 35) - CUT_MARGIN_IN * 2;
    var maxH = PLAN_HEIGHT_IN - CUT_MARGIN_IN * 2;
    var k = Math.min(maxW / logoW, maxH / logoH);
    return {
      artW: logoW * k, artH: logoH * k,
      w: logoW * k + CUT_MARGIN_IN * 2, h: logoH * k + CUT_MARGIN_IN * 2
    };
  }

  /* Plans that would print this logo no bigger than a smaller plan already
     does: a square logo hits the 23-inch height in every plan. */
  function samePlans(logoW, logoH) {
    var a = fitInches(logoW, logoH, 'small');
    var out = [];
    ['medium', 'large'].forEach(function (p) {
      var f = fitInches(logoW, logoH, p);
      if (f.w - a.w < 1) out.push(p);
    });
    return out;
  }

  function roundIn(v) { return Math.round(v); }

  window.MDMockup = {
    CARS: CARS,
    PLAN_WIDTH_IN: PLAN_WIDTH_IN,
    PLAN_HEIGHT_IN: PLAN_HEIGHT_IN,
    CUT_MARGIN_IN: CUT_MARGIN_IN,
    ppi: ppi,
    makeCanvas: makeCanvas,
    toCanvas: toCanvas,
    edgeBackground: edgeBackground,
    prepareLogo: prepareLogo,
    alphaTrim: alphaTrim,
    fitInches: fitInches,
    samePlans: samePlans,
    roundIn: roundIn
  };

  /* ────────────────────────────────────────────────────────────────────────
     Save your design: email capture in the editors.

     mount({
       root:       element the card is appended to,
       getDesign:  function(cb) -> cb({ mockupUrl, plan, cutLabel, carType, restoreUrl, editor }),
       canPrompt:  function() -> true once the customer has a design worth saving
     })

     The profile is identified with Klaviyo's onsite script (public key, no
     secret in the browser), subscribed to the "Mockup leads" list only when
     the box is ticked, and gets a "Mockup Approved" event that starts the
     "Mockup aprobado" flow.
     ──────────────────────────────────────────────────────────────────────── */
  var KLAVIYO_COMPANY = 'XT4axa';
  var KLAVIYO_LIST = 'QSSDLN';
  var SAVED_KEY = 'md_design_saved';
  var PROMPTED_KEY = 'md_exit_prompted';

  function storageGet(store, k) { try { return window[store].getItem(k); } catch (e) { return null; } }
  function storageSet(store, k, v) { try { window[store].setItem(k, v); } catch (e) {} }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v); }

  function klaviyoReady(cb) {
    var tries = 0;
    (function wait() {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') return cb(window.klaviyo);
      if (++tries > 40) return cb(null);
      setTimeout(wait, 250);
    })();
  }

  function subscribe(email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Mockup editor',
            profile: { data: { type: 'profile', attributes: { email: email } } }
          },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
        }
      })
    }).catch(function () {});
  }

  function sendDesign(email, consent, design) {
    return new Promise(function (resolve, reject) {
      klaviyoReady(function (k) {
        if (!k) return reject(new Error('klaviyo'));
        var props = {
          mockup_url: design.mockupUrl || '',
          checkout_url: design.restoreUrl || '',
          plan: design.plan || '',
          cut_label: design.cutLabel || '',
          car_type: design.carType || '',
          editor: design.editor || ''
        };
        Promise.resolve(k.identify({ email: email }))
          .then(function () { return k.track('Mockup Approved', props); })
          .then(function () { return consent ? subscribe(email) : null; })
          .then(function () { storageSet('localStorage', SAVED_KEY, '1'); resolve(); })
          .catch(reject);
      });
    });
  }

  /* Logo maker: the customer's logos and business details go to Klaviyo,
     where the "Logo Created" event starts the follow-up emails. */
  function logoProps(data) {
    return {
      business_name: data.name || '',
      business_line: data.line || '',
      business_about: data.about || '',
      logo_urls: data.logoUrls || [],
      logo_thumbs: (data.logoUrls || []).map(function (u) { return u.replace('/upload/', '/upload/w_560,c_limit,f_jpg,q_auto,b_white/'); }),
      chosen_logo_url: data.chosenUrl || '',
      continue_url: data.continueUrl || '',
      logo_style: data.style || '',
      editor: data.editor || ''
    };
  }

  function sendLogos(email, consent, data) {
    return new Promise(function (resolve, reject) {
      klaviyoReady(function (k) {
        if (!k) return reject(new Error('klaviyo'));
        Promise.resolve(k.identify({ email: email, business_name: data.name || '' }))
          .then(function () { return k.track('Logo Created', logoProps(data)); })
          .then(function () { return consent ? subscribe(email) : null; })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  /* Visitors Klaviyo already knows get the same event without asking again */
  function trackLogosIfKnown(data) {
    klaviyoReady(function (k) {
      if (!k || !k.isIdentified) return;
      Promise.resolve(k.isIdentified()).then(function (known) {
        if (known) k.track('Logo Created', logoProps(data));
      }).catch(function () {});
    });
  }

  function formHtml(id, opts) {
    return '' +
      '<form class="mdsave__form" id="' + id + '" novalidate>' +
        '<label class="mdsave__label" for="' + id + '-email">' + opts.label + '</label>' +
        '<div class="mdsave__row">' +
          '<input class="mdsave__input" id="' + id + '-email" type="email" inputmode="email" autocomplete="email" placeholder="you@business.com" required>' +
          '<button class="mdsave__btn" type="submit">' + opts.button + '</button>' +
        '</div>' +
        '<label class="mdsave__consent"><input type="checkbox" id="' + id + '-consent" checked> Send me tips and offers. Unsubscribe anytime.</label>' +
        '<p class="mdsave__msg" id="' + id + '-msg" role="status" hidden></p>' +
      '</form>';
  }

  function wireForm(form, getDesign, onDone) {
    var input = form.querySelector('input[type=email]');
    var consent = form.querySelector('input[type=checkbox]');
    var btn = form.querySelector('button[type=submit]');
    var msg = form.querySelector('.mdsave__msg');
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input.value || '').trim();
      if (!validEmail(email)) {
        msg.textContent = 'Enter a valid email address.';
        msg.hidden = false; msg.dataset.state = 'error';
        input.focus();
        return;
      }
      btn.disabled = true;
      msg.textContent = 'Saving your design…';
      msg.hidden = false; msg.dataset.state = 'busy';
      getDesign(function (design) {
        sendDesign(email, consent.checked, design || {})
          .then(function () {
            msg.textContent = 'Done. Your design is on its way to ' + email + '.';
            msg.dataset.state = 'ok';
            if (onDone) onDone();
          })
          .catch(function () {
            btn.disabled = false;
            msg.textContent = 'We could not save it right now. Please try again.';
            msg.dataset.state = 'error';
          });
      });
    });
  }

  function mount(opts) {
    if (!opts || !opts.root) return null;
    var getDesign = opts.getDesign || function (cb) { cb({}); };
    var canPrompt = opts.canPrompt || function () { return true; };

    /* 1 · Card next to the buy button */
    var card = document.createElement('div');
    card.className = 'mdsave';
    card.innerHTML =
      '<p class="mdsave__title">Save your design</p>' +
      '<p class="mdsave__text">We will email you this mockup so you can share it or order later.</p>' +
      formHtml('mdsave-card', { label: 'Your email', button: 'Email me my design' });
    opts.root.appendChild(card);
    wireForm(card.querySelector('form'), getDesign, null);

    /* 3 · Small button under the preview that opens the same form */
    if (opts.previewRoot) {
      var quick = document.createElement('button');
      quick.type = 'button';
      quick.className = 'mdsave__quick';
      quick.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 6L2 7"/></svg> Email this mockup';
      quick.addEventListener('click', function () {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var input = card.querySelector('input[type=email]');
        setTimeout(function () { input.focus(); }, 350);
      });
      opts.previewRoot.appendChild(quick);
      quick.hidden = !canPrompt();
      opts.onDesignChange = function () { quick.hidden = !canPrompt(); };
    }

    /* 2 · Exit prompt: once per session, only with a design on screen and
       no email given yet. Desktop: pointer leaving through the top.
       Phones: switching away from the tab. */
    var modal = null;
    function openExit() {
      if (storageGet('sessionStorage', PROMPTED_KEY) || storageGet('localStorage', SAVED_KEY)) return;
      if (!canPrompt()) return;
      storageSet('sessionStorage', PROMPTED_KEY, '1');
      modal = document.createElement('div');
      modal.className = 'mdsave-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'mdsave-exit-title');
      modal.innerHTML =
        '<div class="mdsave-modal__box">' +
          '<button type="button" class="mdsave-modal__close" aria-label="Close">×</button>' +
          '<p class="mdsave__title" id="mdsave-exit-title">Leaving already?</p>' +
          '<p class="mdsave__text">Get this mockup in your inbox, plus 20% off your first order.</p>' +
          formHtml('mdsave-exit', { label: 'Your email', button: 'Send it to me' }) +
        '</div>';
      document.body.appendChild(modal);
      var prev = document.activeElement;
      function close() {
        if (!modal) return;
        modal.remove(); modal = null;
        document.removeEventListener('keydown', onKey);
        if (prev && prev.focus) prev.focus();
      }
      function onKey(e) { if (e.key === 'Escape') close(); }
      modal.querySelector('.mdsave-modal__close').addEventListener('click', close);
      modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
      document.addEventListener('keydown', onKey);
      wireForm(modal.querySelector('form'), getDesign, function () { setTimeout(close, 1800); });
      modal.querySelector('input[type=email]').focus();
    }
    document.addEventListener('mouseout', function (e) {
      if (!e.relatedTarget && e.clientY <= 0) openExit();
    });
    document.addEventListener('visibilitychange', function () {
      /* Shown when they come back, so the prompt is not wasted on a hidden tab. */
      if (document.visibilityState === 'hidden') {
        if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
          window.__mdsaveReturn = true;
        }
      } else if (window.__mdsaveReturn) {
        window.__mdsaveReturn = false;
        openExit();
      }
    });

    return { refresh: function () { if (opts.onDesignChange) opts.onDesignChange(); } };
  }

  window.MDSaveDesign = { mount: mount, sendDesign: sendDesign, sendLogos: sendLogos, trackLogosIfKnown: trackLogosIfKnown };
})();
